import React from 'react';
import ChevronDownIcon from 'app/components/TimelineCard/ChevronDownIcon';
import BlackCheckMarkIcon from 'app/assets/svgIcons/BlackCheckMarkIcon';
import MilestoneMatrixFromAPI from '../Milestones/MilestoneMatrixFromAPI';
import MilestoneMenu from '../Milestones/MilestoneMenu';
import AddMilestoneFooter from '../Milestones/AddMilestoneFooter';
import type { MilestoneSortPreferenceChoice } from '../hooks/useMilestoneSortPreference';
import styles from '../SlideOutPanel.module.scss';

type MilestoneMatrixComponentProps = React.ComponentProps<
  typeof MilestoneMatrixFromAPI
>;

/** Only sort fields from getMilestoneMatrixSortProps — avoids accidental prop override via spread. */
export type SlideoutMilestoneMatrixSortProps = Pick<
  MilestoneMatrixComponentProps,
  'sortBy' | 'onSortChange'
>;

export type SlideoutMilestoneMatrixProps = {
  matrixKey: string;
  refreshToken: number;
  milestones: MilestoneMatrixComponentProps['milestones'];
  onMilestoneUpdate: MilestoneMatrixComponentProps['onMilestoneUpdate'];
  onDataRefresh: MilestoneMatrixComponentProps['onDataRefresh'];
  onNotificationRefresh: MilestoneMatrixComponentProps['onNotificationRefresh'];
  isCommentPanelOpen: MilestoneMatrixComponentProps['isCommentPanelOpen'];
  isEditable: MilestoneMatrixComponentProps['isEditable'];
  issueId: MilestoneMatrixComponentProps['issueId'];
  milestoneId?: MilestoneMatrixComponentProps['milestoneId'];
  isFullScreen: MilestoneMatrixComponentProps['isFullScreen'];
  tier: MilestoneMatrixComponentProps['tier'];
  momentType?: MilestoneMatrixComponentProps['momentType'];
  pco?: MilestoneMatrixComponentProps['pco'];
  currentOverviewMvpStatusContent: MilestoneMatrixComponentProps['currentOverviewMvpStatusContent'];
  productGroups: MilestoneMatrixComponentProps['productGroups'];
  sendForApprovalContext?: MilestoneMatrixComponentProps['sendForApprovalContext'];
  alertHighlightScope?: MilestoneMatrixComponentProps['alertHighlightScope'];
  parentMomentId?: MilestoneMatrixComponentProps['parentMomentId'];
  phaseName?: MilestoneMatrixComponentProps['phaseName'];
  phaseId?: MilestoneMatrixComponentProps['phaseId'];
  sortProps?: SlideoutMilestoneMatrixSortProps;
  wrapperClassName?: string;
  isOpen?: MilestoneMatrixComponentProps['isOpen'];
  phases?: MilestoneMatrixComponentProps['phases'];
  onOpenFeedback?: MilestoneMatrixComponentProps['onOpenFeedback'];
};

/**
 * Single milestone matrix instance — Part 2B-4.
 * Sort/menu state remains in index.tsx; pass sortProps from getMilestoneMatrixSortProps.
 */
export function SlideoutMilestoneMatrix({
  matrixKey,
  refreshToken,
  milestones,
  onMilestoneUpdate,
  onDataRefresh,
  onNotificationRefresh,
  isCommentPanelOpen,
  isEditable,
  issueId,
  milestoneId,
  isFullScreen,
  tier,
  momentType,
  pco,
  currentOverviewMvpStatusContent,
  productGroups,
  alertHighlightScope,
  parentMomentId,
  phaseName,
  phaseId,
  sendForApprovalContext,
  sortProps = {},
  wrapperClassName,
  isOpen,
  phases,
  onOpenFeedback
}: SlideoutMilestoneMatrixProps) {
  const matrix = (
    <MilestoneMatrixFromAPI
      key={matrixKey}
      {...sortProps}
      refreshToken={refreshToken}
      milestones={milestones}
      onMilestoneUpdate={onMilestoneUpdate}
      onDataRefresh={onDataRefresh}
      onNotificationRefresh={onNotificationRefresh}
      isCommentPanelOpen={isCommentPanelOpen}
      isEditable={isEditable}
      issueId={issueId}
      milestoneId={milestoneId}
      isFullScreen={isFullScreen}
      tier={tier}
      momentType={momentType}
      pco={pco}
      currentOverviewMvpStatusContent={currentOverviewMvpStatusContent}
      productGroups={productGroups}
      alertHighlightScope={alertHighlightScope}
      parentMomentId={parentMomentId ?? undefined}
      phaseName={phaseName}
      phaseId={phaseId}
      isOpen={isOpen}
      phases={phases}
      sendForApprovalContext={sendForApprovalContext}
      onOpenFeedback={onOpenFeedback}
    />
  );

  if (wrapperClassName) {
    return <div className={wrapperClassName}>{matrix}</div>;
  }

  return matrix;
}

export type SlideoutInheritedMilestonesPlaceholderProps = {
  wrapperClassName: string;
  placeholderClassName: string;
};

export function SlideoutInheritedMilestonesPlaceholder({
  wrapperClassName,
  placeholderClassName
}: SlideoutInheritedMilestonesPlaceholderProps) {
  return (
    <div
      data-testid="inherited-milestones-placeholder"
      className={wrapperClassName}
    >
      <div
        data-testid="inherited-milestones-no-data"
        className={placeholderClassName}
      >
        No inherited milestones
      </div>
    </div>
  );
}

export type SlideoutMilestoneSortDropdownProps = {
  sortKey: string;
  sortDropdownOpen: Record<string, boolean>;
  getSortDescriptiveText: (key: string) => string;
  getEffectiveMilestoneSort: (key: string) => string;
  handleSortToggle: (key: string) => void;
  handleSortSelect: (key: string, sort: MilestoneSortPreferenceChoice) => void;
  /** Default matches accordion headers: leading space before "Date" */
  dateSortLabel?: string;
};

/**
 * Milestone sort-by dropdown (Part 3B-1).
 */
export function SlideoutMilestoneSortDropdown({
  sortKey,
  sortDropdownOpen,
  getSortDescriptiveText,
  getEffectiveMilestoneSort,
  handleSortToggle,
  handleSortSelect,
  dateSortLabel = ' Date'
}: SlideoutMilestoneSortDropdownProps) {
  return (
    <div
      className={styles.sortByDropdown}
      onClick={() => handleSortToggle(sortKey)}
    >
      <span className={styles.sortByText}>
        {getSortDescriptiveText(sortKey)}
      </span>
      <ChevronDownIcon className="chevronDownIcon" />
      {sortDropdownOpen[sortKey] && (
        <div className={styles.sortDropdown}>
          <button
            className={`${styles.sortOption} ${
              getEffectiveMilestoneSort(sortKey) === 'none'
                ? styles.sortOptionActive
                : ''
            }`}
            onClick={() => handleSortSelect(sortKey, 'none')}
          >
            <span>Default</span>
            {getEffectiveMilestoneSort(sortKey) === 'none' && (
              <BlackCheckMarkIcon className={styles.checkmark} />
            )}
          </button>
          <button
            className={`${styles.sortOption} ${
              getEffectiveMilestoneSort(sortKey) === 'date'
                ? styles.sortOptionActive
                : ''
            }`}
            onClick={() => handleSortSelect(sortKey, 'date')}
          >
            <span>{dateSortLabel}</span>
            {getEffectiveMilestoneSort(sortKey) === 'date' && (
              <BlackCheckMarkIcon className={styles.checkmark} />
            )}
          </button>
        </div>
      )}
    </div>
  );
}

export type SlideoutMilestoneMenuSlotProps = {
  milestones: any[];
  menuKey: string;
  menuReactKey: string;
  momentName?: string;
  milestoneMenuOpen: Record<string, boolean>;
  handleMilestoneMenuToggle: (menuKey: string) => void;
  handleDeleteMilestones: (
    phaseId?: string,
    phaseName?: string
  ) => Promise<void>;
  phaseId?: string;
  phaseName?: string;
};

/**
 * Conditional MilestoneMenu wrapper (Part 3B-1).
 */
export function SlideoutMilestoneMenuSlot({
  milestones,
  menuKey,
  menuReactKey,
  momentName,
  milestoneMenuOpen,
  handleMilestoneMenuToggle,
  handleDeleteMilestones,
  phaseId,
  phaseName
}: SlideoutMilestoneMenuSlotProps) {
  const hasMilestones = milestones && milestones.length > 0;
  if (!hasMilestones) return null;

  return (
    <MilestoneMenu
      key={menuReactKey}
      milestones={milestones}
      momentName={momentName}
      isOpen={milestoneMenuOpen[menuKey] || false}
      onToggle={() => handleMilestoneMenuToggle(menuKey)}
      onDelete={handleDeleteMilestones}
      phaseId={phaseId}
      phaseName={phaseName}
    />
  );
}

export type SlideoutMilestoneSortAndMenuProps =
  SlideoutMilestoneSortDropdownProps &
    SlideoutMilestoneMenuSlotProps & {
      controlsGap?: number | string;
      stopPropagation?: boolean;
    };

/**
 * Sort dropdown + milestone menu toolbar (Part 3B-1).
 */
export function SlideoutMilestoneSortAndMenu({
  sortKey,
  sortDropdownOpen,
  getSortDescriptiveText,
  getEffectiveMilestoneSort,
  handleSortToggle,
  handleSortSelect,
  dateSortLabel,
  milestones,
  menuKey,
  menuReactKey,
  momentName,
  milestoneMenuOpen,
  handleMilestoneMenuToggle,
  handleDeleteMilestones,
  phaseId,
  phaseName,
  controlsGap = '20px',
  stopPropagation = false
}: SlideoutMilestoneSortAndMenuProps) {
  return (
    <div
      data-testid="milestone-section-controls"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: controlsGap,
        marginLeft: 'auto'
      }}
      onClick={stopPropagation ? e => e.stopPropagation() : undefined}
    >
      <SlideoutMilestoneSortDropdown
        sortKey={sortKey}
        sortDropdownOpen={sortDropdownOpen}
        getSortDescriptiveText={getSortDescriptiveText}
        getEffectiveMilestoneSort={getEffectiveMilestoneSort}
        handleSortToggle={handleSortToggle}
        handleSortSelect={handleSortSelect}
        dateSortLabel={dateSortLabel}
      />
      <SlideoutMilestoneMenuSlot
        milestones={milestones}
        menuKey={menuKey}
        menuReactKey={menuReactKey}
        momentName={momentName}
        milestoneMenuOpen={milestoneMenuOpen}
        handleMilestoneMenuToggle={handleMilestoneMenuToggle}
        handleDeleteMilestones={handleDeleteMilestones}
        phaseId={phaseId}
        phaseName={phaseName}
      />
    </div>
  );
}

export type SlideoutMilestoneAccordionHeaderProps = {
  titleSlot: React.ReactNode;
  showControls?: boolean;
} & SlideoutMilestoneSortAndMenuProps;

/**
 * Milestone section header inside phase/non-phased accordions (Part 3B-1).
 */
export function SlideoutMilestoneAccordionHeader({
  titleSlot,
  showControls = true,
  ...sortAndMenuProps
}: SlideoutMilestoneAccordionHeaderProps) {
  return (
    <div className={styles.milestonesHeader}>
      <div className={styles.milestonesTitle}>{titleSlot}</div>
      {showControls && <SlideoutMilestoneSortAndMenu {...sortAndMenuProps} />}
    </div>
  );
}

export type SlideoutMilestoneAddFooterSlotProps = {
  visible: boolean;
  onDataRefresh: (patchResponse?: unknown) => void;
  issueId: string;
  milestoneId?: string;
  phaseId?: string;
  phaseName?: string;
  momentName: string;
  momentType: string;
  disabled?: boolean;
};

/**
 * Conditional AddMilestoneFooter (Part 3B-1).
 */
export function SlideoutMilestoneAddFooterSlot({
  visible,
  onDataRefresh,
  issueId,
  milestoneId,
  phaseId,
  phaseName,
  momentName,
  momentType,
  disabled
}: SlideoutMilestoneAddFooterSlotProps) {
  if (!visible) return null;

  return (
    <AddMilestoneFooter
      onDataRefresh={onDataRefresh}
      issueId={issueId}
      milestoneId={milestoneId}
      phaseId={phaseId}
      phaseName={phaseName}
      momentName={momentName}
      momentType={momentType}
      disabled={disabled}
    />
  );
}
